import { map, timer, of } from 'rxjs'
import {
  tap,
  switchMap,
  mergeMap,
  take,
  withLatestFrom,
  catchError,
  filter,
  takeWhile,
  startWith,
  takeUntil,
  debounceTime
} from 'rxjs/operators'
import Actions from '../actions'
import { ofType } from 'redux-observable'
import { getPayload, catchAndDispatchError } from '../utils/rxUtils'
import loginService from '../services/loginService'
import apis from '../apis'
import {
  doesUserInteracted,
  isWarningModalOpened,
  calculateTimeUntilAutoRefresh
} from 'utils/authUtils'

export const handleTokenReceived = action$ =>
  action$.pipe(
    getPayload(
      Actions.APPLICATION_INIT_RECEIVED,
      Actions.WELCOME_BACK_RECEIVED
    ),
    filter(exists => exists),
    tap(({ token }) => loginService.setTokens({ token })),
    map(({ origin }) =>
      Actions.createAction(Actions.AUTHENTICATED_SUCCESSFULY, {
        origin
      })
    )
  )

export const applicationReload = action$ =>
  action$.pipe(
    getPayload(Actions.APP_CONFIG_RECEIVED),
    map(() => loginService.getTokenExpiry()),
    filter(exists => exists),
    map(expiryTime =>
      Actions.createAction(Actions.TOKEN_EXPIRY_UPDATED, expiryTime)
    )
  )

export const saveExpiryTime = action$ =>
  action$.pipe(
    getPayload(
      Actions.APPLICATION_INIT_RECEIVED,
      Actions.REFRESH_TOKEN_RECEIVED,
      Actions.WELCOME_BACK_RECEIVED
    ),
    map(({ token }) => {
      return Actions.createAction(
        Actions.TOKEN_EXPIRY_UPDATED,
        token?.access_token_exp
      )
    })
  )

export const handleRefreshToken = action$ =>
  action$.pipe(
    getPayload(Actions.REFRESH_TOKEN_REQUESTED),
    map(() => loginService.getRefreshToken()),
    switchMap(refreshToken => {
      return apis.refreshToken({ refreshToken }).pipe(
        tap(token => loginService.setTokens({ token })),
        map(token =>
          Actions.createAction(Actions.REFRESH_TOKEN_RECEIVED, { token })
        ),
        catchAndDispatchError
      )
    })
  )

export const handleWelcomeBack = action$ =>
  action$.pipe(
    getPayload(Actions.WELCOME_BACK_REQUESTED),
    switchMap(({ applicationId, memberAuthInfo, route }) => {
      return apis
        .authenticateWelcomeBack({
          applicationId,
          ...memberAuthInfo
        })
        .pipe(
          tap(token => {
            loginService.setTokens(token)
          }),
          mergeMap(({ token }) => {
            return [
              Actions.createAction(Actions.WELCOME_BACK_RECEIVED, {
                route,
                applicationId,
                token,
                origin: 'welcome_back'
              }),
              Actions.createAction(Actions.APPLICATION_DATA_REQUESTED, {
                applicationId
              })
            ]
          }),
          catchError(error => {
            if (error?.status === 400) {
              return of(
                Actions.createAction(Actions.WELCOME_BACK_FAILED, {
                  error: error.body?.error
                })
              )
            }
            // If the error is not 400, rethrow it
            throw error
          }),
          catchAndDispatchError
        )
    })
  )

// this function wil start a timer once token is received
// and will be reseted on each user interaction.
// if the timer expires the user will be logged out
export const startTokenExpirationTimer = (action$, state$) =>
  action$.pipe(
    ofType(Actions.TOKEN_EXPIRY_UPDATED),
    withLatestFrom(state$),
    switchMap(([_, state]) => {
      const accessTokenLifetime = state.app.appConfig.access_token_lifetime
      const inactivityTimeoutWarning =
        state.app.appConfig.inactivity_timeout_warning
      return action$.pipe(
        takeUntil(
          action$.pipe(
            ofType(Actions.LOGOUT_RECEIVED, Actions.OPERATION_FAILED)
          )
        ),
        ofType(Actions.USER_INTERACTED),
        startWith(Actions.USER_INTERACTED),
        debounceTime((accessTokenLifetime - inactivityTimeoutWarning) * 1000),
        take(1),
        withLatestFrom(state$),
        filter(([_, state]) => !state.application.isFlowCompleted),
        mergeMap(() => [
          Actions.createAction(Actions.TIMEOUT_MODAL_OPENED),
          Actions.createAction(Actions.SAVE_PROGRESS_DIALOG_CLOSED)
        ])
      )
    })
  )

// in case the user does interacted with the app,
// the token will be refreshed automaticly
export const autoRefreshTokenEpic = (action$, state$) =>
  action$.pipe(
    getPayload(Actions.TOKEN_EXPIRY_UPDATED),
    withLatestFrom(state$),
    switchMap(([expiryTime, state]) => {
      const timeUntilAutoRefresh = calculateTimeUntilAutoRefresh(expiryTime)
      return timer(timeUntilAutoRefresh * 1000).pipe(
        takeWhile(() => !isWarningModalOpened(state)),
        filter(() => doesUserInteracted(state)),
        switchMap(() => {
          return of(Actions.createAction(Actions.REFRESH_TOKEN_REQUESTED))
        })
      )
    })
  )

export const logoutOnTimeout = action$ =>
  action$.pipe(
    ofType(Actions.LOGOUT_REQUESTED),
    switchMap(() => {
      return apis.logout().pipe(
        tap(() => loginService.clearTokens()),
        map(() => Actions.createAction(Actions.LOGOUT_RECEIVED))
      )
    }),
    catchError(error => {
      console.error(error)
      tap(() => loginService.clearTokens()) // clear them anyway
      return of(Actions.createAction(Actions.LOGOUT_RECEIVED))
    })
  )